/**
 * Cloudflare Images URL Utility
 *
 * Provides isomorphic (Node + Vite) functions for resolving image references
 * to optimized delivery URLs via Cloudflare Images Flexible Variants.
 *
 * Image reference format:
 * - `cf:<uuid>` — Cloudflare Images ID (strips prefix, builds delivery URL)
 * - `https://imagedelivery.net/...` — Already a CF delivery URL (re-parameterizes)
 * - Any other string — Returned as-is (R2 URL, external URL, etc.)
 */
const CF_PREFIX = 'cf:';
const R2_DEV_PATTERN = /^https?:\/\/pub-[a-f0-9]+\.r2\.dev\//;
let _missingHashReported = false;
/**
 * Size presets — single source of truth for image dimensions.
 * Use preset names instead of magic numbers throughout the codebase.
 */
export const IMAGE_PRESETS = {
    thumb: { width: 200, quality: 75 },
    card: { width: 400, quality: 80 },
    hero: { width: 800, quality: 85 },
    og: { width: 1200, quality: 85 },
    zoom: { width: 1600, quality: 90 },
};
/** Module-level cache for account hash, set once via setCfAccountHash() or auto-detected */
let _accountHash;
/** Module-level R2 public base URL for normalizing r2.dev → custom domain */
let _r2PublicBase;
/**
 * Explicitly set the CF Images account hash.
 *
 * Call this once at app startup in environments where automatic detection
 * doesn't work (e.g., Vite frontends, CF Workers).
 *
 * @example
 * // In storefront main.tsx:
 * setCfAccountHash(import.meta.env.VITE_CF_IMAGES_ACCOUNT_HASH || '')
 */
export function setCfAccountHash(hash) {
    _accountHash = hash;
}
/**
 * Set the R2 custom domain base URL (e.g., 'https://media.agciceyim.az/').
 * Used by normalizeImageUrl to rewrite raw r2.dev URLs.
 */
export function setR2PublicBase(base) {
    _r2PublicBase = base.endsWith('/') ? base : base + '/';
}
/**
 * Normalize image URLs for API responses and rendering.
 *
 * Handles two transformations:
 * 1. `cf:<uuid>` → Cloudflare Images delivery URL (prevents ERR_UNKNOWN_URL_SCHEME)
 * 2. `r2.dev` → custom domain (rate-limited dev URLs → production domain)
 *
 * This is the single chokepoint for all image URL normalization.
 * Called by backend normalizeImages.ts before sending API responses,
 * ensuring clients never receive raw `cf:` refs.
 */
export function normalizeImageUrl(url) {
    if (!url)
        return '';
    // Resolve cf: refs to delivery URLs (must happen before r2.dev rewrite)
    if (url.startsWith(CF_PREFIX)) {
        const imageId = url.slice(CF_PREFIX.length);
        const hash = getAccountHash();
        if (hash) {
            return buildDeliveryUrl(hash, imageId);
        }
        // No hash available — log once and return empty to prevent ERR_UNKNOWN_URL_SCHEME
        if (!_missingHashReported) {
            _missingHashReported = true;
            console.error('[imageUrl] normalizeImageUrl: missing CF account hash, cannot resolve', url);
        }
        return '';
    }
    // Rewrite r2.dev → custom domain
    const base = _r2PublicBase || (typeof process !== 'undefined' && process.env?.R2_PUBLIC_BASE) || '';
    if (base && R2_DEV_PATTERN.test(url)) {
        return url.replace(R2_DEV_PATTERN, base);
    }
    return url;
}
/**
 * Resolve CF Images account hash from environment (isomorphic).
 *
 * Resolution order:
 * 1. Module-level cache (set via setCfAccountHash())
 * 2. process.env.CF_IMAGES_ACCOUNT_HASH (Node.js / backend)
 * 3. Empty string fallback (graceful degradation)
 */
const getAccountHash = () => {
    // 1. Explicit setter (Vite frontends, CF Workers)
    if (_accountHash !== undefined)
        return _accountHash;
    // 2. Node.js (backend, SSR)
    if (typeof process !== 'undefined' && process.env?.CF_IMAGES_ACCOUNT_HASH) {
        return process.env.CF_IMAGES_ACCOUNT_HASH;
    }
    // 3. Fallback
    return '';
};
/**
 * Build a CF Images delivery URL with Flexible Variants parameters.
 */
function buildDeliveryUrl(hash, imageId, width, quality) {
    const params = ['format=auto'];
    if (width)
        params.push(`w=${width}`);
    params.push(`quality=${quality || 80}`);
    // Encode imageId to handle slashes, spaces, and special chars in custom IDs
    const safeId = encodeURIComponent(imageId);
    return `https://imagedelivery.net/${hash}/${safeId}/${params.join(',')}`;
}
/**
 * Re-parameterize an existing CF delivery URL with new width/quality.
 * Replaces the last path segment (the variant string).
 */
function reparameterizeDeliveryUrl(url, width, quality) {
    const parts = url.split('/');
    const params = ['format=auto'];
    if (width)
        params.push(`w=${width}`);
    params.push(`quality=${quality || 80}`);
    parts[parts.length - 1] = params.join(',');
    return parts.join('/');
}
/**
 * Resolve width and quality from a preset name or explicit options.
 */
function resolveOptions(preset) {
    if (typeof preset === 'string') {
        const p = IMAGE_PRESETS[preset];
        return { width: p.width, quality: p.quality };
    }
    return { width: preset.width, quality: preset.quality || 80 };
}
/**
 * Get an optimized image delivery URL.
 *
 * Handles three reference formats:
 * 1. `cf:<uuid>` — CF Image ID → builds delivery URL
 * 2. `https://imagedelivery.net/...` — Re-parameterizes variant
 * 3. Any other URL — Returns as-is (R2, external, etc.)
 *
 * @param ref - Image reference (cf:<id>, delivery URL, or plain URL)
 * @param preset - Preset name ('thumb'|'card'|'hero'|'og'|'zoom') or { width, quality }
 * @returns Optimized delivery URL or original URL as fallback
 *
 * @example
 * getImageUrl('cf:a1b2c3d4-e5f6-...', 'card')
 * // → 'https://imagedelivery.net/HASH/a1b2c3d4-e5f6-.../format=auto,w=400,quality=80'
 *
 * @example
 * getImageUrl('https://media.agciceyim.az/bouquets/abc.jpg', 'hero')
 * // → 'https://media.agciceyim.az/bouquets/abc.jpg' (passthrough)
 */
export function getImageUrl(ref, preset = 'card') {
    if (!ref)
        return '';
    // Normalize r2.dev URLs before any processing
    ref = normalizeImageUrl(ref);
    const { width, quality } = resolveOptions(preset);
    // Case 1: CF Image ID with prefix
    if (ref.startsWith(CF_PREFIX)) {
        const imageId = ref.slice(CF_PREFIX.length);
        const hash = getAccountHash();
        if (!hash) {
            // Never leak cf: refs to the browser — causes ERR_UNKNOWN_URL_SCHEME.
            // Log once to make misconfiguration visible without flooding console.
            if (!_missingHashReported) {
                _missingHashReported = true;
                console.error('[imageUrl] Missing CF Images account hash. Cannot resolve cf: image references.', ref);
            }
            return '';
        }
        return buildDeliveryUrl(hash, imageId, width, quality);
    }
    // Case 2: Already a CF delivery URL — re-parameterize
    if (ref.includes('imagedelivery.net')) {
        return reparameterizeDeliveryUrl(ref, width, quality);
    }
    // Case 3: R2 URL, external URL, or unknown — passthrough
    return ref;
}
/**
 * Generate srcSet string for responsive images.
 *
 * Only generates srcSet for CF Images references (cf: prefix or imagedelivery.net).
 * R2/external URLs return undefined (no srcSet available).
 *
 * @param ref - Image reference
 * @param widths - Array of pixel widths (default: [400, 800, 1200, 1600])
 * @param quality - Quality for all variants (default: 80)
 * @returns srcSet string or undefined if not a CF image
 */
export function getImageSrcSet(ref, widths = [400, 800, 1200, 1600], quality = 80) {
    if (!ref)
        return undefined;
    // Only generate srcSet for CF images
    if (!ref.startsWith(CF_PREFIX) && !ref.includes('imagedelivery.net')) {
        return undefined;
    }
    const entries = widths
        .map(w => {
        const url = getImageUrl(ref, { width: w, quality });
        return url ? `${url} ${w}w` : null;
    })
        .filter(Boolean);
    return entries.length ? entries.join(', ') : undefined;
}
